import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, Send, ChevronLeft } from 'lucide-react';
import { gapOf, readable, renderPhrase } from './banterVocabulary';

/**
 * Quick Banter: a bottom sheet on a phone, a dialog on a desktop.
 *
 * Theme first, then a line, then - only for a line with a gap - who it is about (backlog item 63,
 * the owner's order). A line is shown with its gap as a blank while it is being chosen, because a
 * blank in a list of options reads as a question; once somebody is chosen, the preview is a real
 * sentence about a real person. The themes and their order are the admin's, from the console's
 * Banter lines screen; nothing here sorts or draws at random.
 *
 * There is still no text box. Choosing from a vetted vocabulary is what means there is nothing to
 * moderate after the fact, so the composer that opens this is a button rather than an input, and
 * the last two taps are select-then-send rather than select-and-send — a mis-tap should cost a
 * correction, not a post.
 */
function QuickBanterSheet({ themes, members, teams, currentUserId, onPost, posting, onClose }) {
  const [themeId, setThemeId] = useState(null);
  const [lineId, setLineId] = useState(null);
  const [targetPlayerId, setTargetPlayerId] = useState('');
  const [targetTeamId, setTargetTeamId] = useState('');
  const sheetRef = useRef(null);

  // You can name anyone on the board except yourself — self-targeting reads as a bug rather than
  // a joke. The list is the board's own, resolved server-side, so a league's game offers the
  // league rather than that game's roster.
  const targetable = useMemo(
    () => members.filter((m) => String(m.playerId) !== String(currentUserId)),
    [members, currentUserId]
  );

  const theme = themes.find((t) => String(t.id) === String(themeId)) || null;
  const line = theme?.phrases.find((p) => String(p.id) === String(lineId)) || null;
  const gap = line ? gapOf(line) : null;
  const player = targetable.find((m) => String(m.playerId) === String(targetPlayerId));
  const team = teams.find((t) => String(t.id) === String(targetTeamId));
  const ready = line && (gap === null || (gap === 'player' && player) || (gap === 'team' && team));
  const names = { playerName: player?.playerName, teamName: team?.name };

  const chooseLine = (id) => {
    setLineId(id);
    setTargetPlayerId('');
    setTargetTeamId('');
  };

  const submit = async () => {
    if (!ready || posting) return;
    const ok = await onPost({
      phraseId: line.id,
      targetPlayerId: gap === 'player' ? Number(player.playerId) : undefined,
      targetTeamId: gap === 'team' ? Number(team.id) : undefined,
    });
    if (ok) onClose();
  };

  // One step back at a time: from who to the lines, from the lines to the themes.
  const back = () => {
    if (line && gap) {
      chooseLine(null);
    } else {
      setThemeId(null);
      chooseLine(null);
    }
  };

  // Escape closes, and focus starts inside rather than behind — a sheet you can open
  // from the keyboard and not get out of is worse than no sheet.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    sheetRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const choosingWho = line && gap;

  return (
    <div className="qb-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="qb-sheet"
        role="dialog"
        aria-modal="true"
        aria-label="Quick Banter"
        tabIndex={-1}
        ref={sheetRef}
      >
        <div className="qb-head">
          {theme ? (
            <button type="button" className="qb-icon-btn" onClick={back} aria-label="Back">
              <ChevronLeft size={20} />
            </button>
          ) : (
            <span className="qb-icon-spacer" aria-hidden="true" />
          )}
          <h2 className="qb-title">{theme ? theme.name : 'Quick Banter'}</h2>
          <button type="button" className="qb-icon-btn" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="qb-body">
          {!theme && (
            <fieldset className="qb-step">
              <legend className="qb-legend">What kind of banter?</legend>
              <div className="qb-aims">
                {themes.map((t) => (
                  <button key={t.id} type="button" className="qb-aim" onClick={() => setThemeId(t.id)}>
                    <span className="qb-aim-label">{t.name}</span>
                  </button>
                ))}
              </div>
            </fieldset>
          )}

          {theme && !choosingWho && (
            <fieldset className="qb-step">
              <legend className="qb-legend">Pick a line</legend>
              <ul className="qb-suggestions">
                {theme.phrases.map((p) => (
                  <li key={p.id}>
                    <button
                      type="button"
                      aria-pressed={String(lineId) === String(p.id)}
                      className={`qb-suggestion${String(lineId) === String(p.id) ? ' on' : ''}`}
                      onClick={() => chooseLine(p.id)}
                    >
                      <span className="qb-suggestion-text">{readable(p)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </fieldset>
          )}

          {choosingWho && (
            <>
              <p className="qb-aimed">{readable(line)}</p>
              {gap === 'player' ? (
                <fieldset className="qb-step">
                  <legend className="qb-legend">Which player?</legend>
                  <div className="qb-targets">
                    {targetable.map((m) => (
                      <button
                        key={m.playerId}
                        type="button"
                        aria-pressed={String(targetPlayerId) === String(m.playerId)}
                        className={`qb-target${String(targetPlayerId) === String(m.playerId) ? ' on' : ''}`}
                        onClick={() => setTargetPlayerId(m.playerId)}
                      >
                        {m.playerName}
                      </button>
                    ))}
                  </div>
                  {targetable.length === 0 && (
                    // Scope-neutral: the same sheet opens on a league board and on a
                    // standalone game's, and "in the league" is wrong on half of them.
                    <p className="qb-none">Nobody else on this board yet.</p>
                  )}
                </fieldset>
              ) : (
                <div className="qb-step">
                  <label className="qb-field">
                    <span className="qb-legend">Which team?</span>
                    <select value={targetTeamId} onChange={(e) => setTargetTeamId(e.target.value)}>
                      <option value="">Pick a team…</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        {ready && (
          <div className="qb-foot">
            <p className="qb-preview">{renderPhrase(line, names)}</p>
            <button
              type="button"
              className="primary-btn qb-post"
              onClick={submit}
              disabled={posting}
            >
              <Send size={16} aria-hidden="true" />
              {posting ? 'Posting…' : 'Post'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuickBanterSheet;
